import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ChevronDownIcon, XIcon } from './Icons';

export interface SelectOption {
  value: string | number;
  label: string;
  /** Optional icon shown before the label in trigger and dropdown */
  icon?: React.ReactNode;
  /** Secondary line (muted) — used in dropdown; trigger shows label + short description when set */
  description?: string;
}

interface SelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  name?: string;
  value?: string | number;
  onChange?: (event: { target: { name?: string; value: string; type: string } }) => void;
  /** Called when the dropdown closes (after select, clear, or click outside). Receives current value. */
  onClose?: (value: string | number) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  showClearButton?: boolean;
  error?: string;
  helperText?: string;
  /** Minimum dropdown panel width in px (default 280). Use 0 to match trigger width. */
  dropdownMinWidth?: number;
  /** Hide search box for short option lists */
  searchable?: boolean;
}

const Select: React.FC<SelectProps> = ({
  name,
  value,
  onChange,
  onClose,
  options = [],
  placeholder = 'Select...',
  searchPlaceholder = 'Type to search...',
  disabled = false,
  readOnly = false,
  required = false,
  showClearButton = true,
  error,
  helperText,
  dropdownMinWidth = 280,
  searchable = true,
  className,
  ...props
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownRect, setDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Normalize so we always have an array of { value, label } and can display when options load
  const normalizedOptions: SelectOption[] = Array.isArray(options)
    ? options.map((o) => ({
        value: o?.value ?? '',
        label: typeof o?.label === 'string' ? o.label : String(o?.value ?? ''),
        icon: o?.icon,
        description: typeof o?.description === 'string' ? o.description : undefined,
      }))
    : [];

  const valueStr = value !== undefined && value !== null ? String(value) : '';
  const selectedOption = normalizedOptions.find((o) => String(o.value) === valueStr);
  const hasValue = selectedOption !== undefined;

  const q = searchQuery.trim().toLowerCase();
  const filteredOptions = q
    ? normalizedOptions.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.description && o.description.toLowerCase().includes(q))
      )
    : normalizedOptions;

  const showSearch = searchable && normalizedOptions.length > 6;

  const updateDropdownRect = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const horizontalPad = 16;
    const viewportPad = 8;
    const gap = 6;
    const maxPanel = Math.min(vw - horizontalPad * 2, 36 * 16);
    const minPanel = dropdownMinWidth;
    const width = Math.min(Math.max(rect.width, minPanel), maxPanel);
    let left = rect.left;
    if (left + width > vw - horizontalPad) {
      left = Math.max(horizontalPad, vw - horizontalPad - width);
    }

    const optionCount = Math.max(filteredOptions.length, 1);
    const searchBlock = showSearch ? 52 : 0;
    const estimatedContent = searchBlock + optionCount * 42 + 12;
    const preferredMax = Math.min(320, Math.max(120, estimatedContent));

    const spaceBelow = vh - rect.bottom - gap - viewportPad;
    const spaceAbove = rect.top - gap - viewportPad;
    const openAbove = spaceBelow < preferredMax && spaceAbove > spaceBelow;

    let maxHeight: number;
    let top: number;
    if (openAbove) {
      maxHeight = Math.min(preferredMax, spaceAbove);
      top = Math.max(viewportPad, rect.top - gap - maxHeight);
      maxHeight = Math.min(maxHeight, rect.top - gap - top);
    } else {
      top = rect.bottom + gap;
      maxHeight = Math.min(preferredMax, spaceBelow);
      if (top + maxHeight > vh - viewportPad) {
        maxHeight = Math.max(120, vh - viewportPad - top);
      }
    }

    setDropdownRect({
      top,
      left,
      width,
      maxHeight: Math.max(80, maxHeight),
    });
  }, [dropdownMinWidth, filteredOptions.length, showSearch]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      const raf = requestAnimationFrame(() => updateDropdownRect());
      const t = showSearch
        ? setTimeout(() => searchInputRef.current?.focus(), 0)
        : undefined;
      const onScrollOrResize = () => {
        updateDropdownRect();
      };
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        cancelAnimationFrame(raf);
        if (t) clearTimeout(t);
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    } else {
      setDropdownRect(null);
    }
  }, [isOpen, showSearch, updateDropdownRect]);

  useEffect(() => {
    if (isOpen) updateDropdownRect();
  }, [isOpen, filteredOptions.length, updateDropdownRect]);

  const handleSelect = (option: SelectOption) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || readOnly) return;
    const newVal = String(option.value);
    onChange?.({ target: { name, value: newVal, type: 'select-one' } });
    setSearchQuery('');
    setIsOpen(false);
    if (onClose) onClose(newVal);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (disabled || readOnly) return;
    onChange?.({ target: { name, value: '', type: 'select-one' } });
    setIsOpen(false);
    if (onClose) onClose('');
  };

  const isDisabled = disabled || readOnly;

  return (
    <div ref={containerRef} className="relative w-full min-w-0" {...props}>
      <input type="hidden" name={name} value={valueStr} readOnly required={required} />

      <div
        className={cn(
          'flex items-center w-full rounded-lg transition-all duration-200 border min-h-10',
          'px-2.5 py-2 text-sm',
          colors.surface,
          colors.border,
          isOpen && 'ring-2 shadow-sm',
          isOpen && colors.primaryBorder,
          !isOpen && !error && colors.interactiveHover,
          error && 'border-red-500',
          isDisabled && 'opacity-50 cursor-not-allowed',
          isDisabled && colors.surfaceHover,
          !isDisabled && 'cursor-pointer',
          className
        )}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <div className={cn('flex items-center gap-1 flex-1 min-w-0', !hasValue && 'opacity-70')}>
          <ChevronDownIcon
            className={cn(
              'w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200',
              isOpen && 'rotate-180',
              hasValue ? colors.primaryFg : colors.textMuted
            )}
          />
          <div className="flex-1 min-w-0 truncate flex flex-col items-start justify-center gap-0 min-h-[1.25rem]">
            {hasValue ? (
              <>
                <div className="flex items-center gap-2 min-w-0 w-full">
                  {selectedOption!.icon != null && (
                    <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
                      {selectedOption!.icon}
                    </span>
                  )}
                  <span className={cn('font-medium text-sm truncate w-full', colors.primaryFg)}>
                    {selectedOption!.label}
                  </span>
                </div>
                {selectedOption!.description ? (
                  <span className={cn('text-xs truncate w-full pl-0', colors.textMuted)}>
                    {selectedOption!.description}
                  </span>
                ) : null}
              </>
            ) : (
              <span className={cn('text-sm', colors.textMuted)}>{placeholder}</span>
            )}
          </div>
        </div>
        {showClearButton && hasValue && !isDisabled && (
          <button
            type="button"
            onClick={handleClear}
            className={cn('ml-0.5 p-1.5 rounded-md flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors', colors.textMuted, colors.interactiveHover, colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '')}
            title="Clear"
            tabIndex={-1}
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {isOpen && !isDisabled && dropdownRect && createPortal(
        <div
          ref={dropdownRef}
          className={cn(
            'fixed z-[9999] rounded-xl shadow-lg border overflow-hidden flex flex-col',
            colors.surface,
            colors.border
          )}
          style={{
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: dropdownRect.width,
            maxHeight: dropdownRect.maxHeight,
          }}
        >
          {showSearch && (
          <div className={cn('p-2 border-b shrink-0', colors.border)} onClick={(e) => e.stopPropagation()}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder={searchPlaceholder}
              className={cn(
                'w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-offset-0',
                colors.border,
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
                colors.surface,
                colors.border,
                colors.text
              )}
              aria-label="Search options"
            />
          </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {normalizedOptions.length === 0 ? (
              <div className={cn('px-3 py-2 text-sm', colors.textMuted)}>No options</div>
            ) : filteredOptions.length === 0 ? (
              <div className={cn('px-3 py-2 text-sm', colors.textMuted)}>No matches</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = String(option.value) === valueStr;
                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    onClick={handleSelect(option)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 text-sm transition-colors flex items-start gap-2',
                      isSelected
                        ? cn(colors.primary, colors.primaryText, 'font-medium')
                        : cn(colors.text, colors.interactiveHover)
                    )}
                  >
                    {option.icon != null && (
                      <span className="flex-shrink-0 w-4 h-4 mt-0.5 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4 opacity-80">
                        {option.icon}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 flex flex-col items-start gap-0.5">
                      <span className="w-full leading-snug">{option.label}</span>
                      {option.description ? (
                        <span
                          className={cn(
                            'w-full text-xs leading-snug font-normal',
                            isSelected ? 'opacity-90' : colors.textMuted
                          )}
                        >
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}

      <div className="mt-1 flex flex-col gap-0.5">
        {error && <div className={cn('text-sm', colors.error)}>{error}</div>}
        {!error && helperText && <div className={cn('text-sm', colors.textMuted)}>{helperText}</div>}
      </div>
    </div>
  );
};

Select.displayName = 'Select';

export { Select };
